import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import {
  BUSY,
  INVALID_REQUEST,
  OUTCOME_UNKNOWN,
  TOKEN_INVALID,
  UNAVAILABLE,
  cardActionBiEvent,
  checkedAct,
  executeCardAction,
  prepareCardAction,
  undoPrepareCardAction,
  type CardActionStage,
  type PreparedCardAction,
} from '../../../lib/card-actions';
import { clearWidgetToolResults } from '../../../lib/widgets';
import { Toast } from '../ui';
import { CardActionDialog } from './CardActionDialog';
import { DashboardVisibleContext } from './dashboard-visible';

// How long a reversible write offers "Undo". The harness honours the undo token for longer, so a press
// near the end still prepares.
const UNDO_WINDOW_MS = 30_000;
const PREPARE_LOAD = 'card-action-prepare';

/** A frame's `{kind:'act'}` message, after the host checked it came from its own frame. */
export interface CardActMessage {
  widget: string;
  op: string;
  record: string;
  /** The component version the card was rendered from. */
  version: number;
  /** The card's own chat prompt with its row's ids filled in, used when the harness refuses without one. */
  fallback: string;
}

/** `prompt` is where a refusal sends the owner: the harness's fallback, else the frame's, and none for an undo. */
type Open = { prepared: PreparedCardAction; op: string; record: string; undo: boolean; prompt: string };
type Notice = { message: string; severity: 'success' | 'error'; undo?: { token: string; op: string; record: string } };

/**
 * A dashboard host's half of a card's direct write: prepare with the harness, the app's own confirm,
 * execute, and Undo. Everything the harness returns stays here: the host never hands any of it to the
 * frame, which only ever sent the request.
 *
 * One at a time per host: an `act` is refused while another is being prepared, confirmed or executed, or
 * while a widget's own confirm is open, and a widget confirm is refused (answered "no") while an act is
 * under way. Two dialogs on one screen would let a card's confirm stand in for the app's.
 *
 * Only while its dashboard is on screen: the host keeps a dashboard it left mounted, hidden, and the dialog,
 * the notice and a widget's confirm draw over the whole page. Leaving cancels an open dialog or widget
 * confirm and drops the notice; a prepare that answers after that is dropped, and a hidden frame's act or
 * confirm is refused.
 *
 * `dashboardSlug` is the one the host rendered: the card's widget is resolved inside it, never from the frame.
 * `onFallback` is the host's own chat hand-off, the one a widget prompt uses: a refusal that wrote nothing sends
 * the card's fallback prompt there, so the button still does what it did before cards could act. An undo, or
 * an execute whose outcome is unknown, never does: that prompt asks for the write itself.
 * `onWritten` is the host's own reload, run after a write so the card shows the record as it now is; the
 * widgets' cached results are dropped first, or the reload would paint the old row again.
 */
export function useCardAction({
  dashboardSlug,
  onFallback,
  onWritten,
}: {
  dashboardSlug: string;
  onFallback: (prompt: string) => void;
  onWritten: () => void;
}) {
  const { t } = useTranslation();
  const { logItemSeen, logClickCta, logPageClickBi, logLoadStart, logLoadFinish } = useGenericBi();
  const [dialog, setDialog] = useState<Open | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Refs, not state, because the hosts' message listeners are bound once and must see the current answer.
  const busyRef = useRef(false);
  const dialogRef = useRef<Open | null>(null);
  // The prepare or undo-prepare waiting for its answer. Hiding the dashboard drops it, and an answer for one
  // that is no longer here is ignored.
  const preparingRef = useRef<{ op: string; record: string; undo: boolean } | null>(null);
  const visible = useContext(DashboardVisibleContext);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  // The widget confirm on screen, if any. Only that one's settling clears it: a newer confirm replaces an
  // older one, which settles first.
  const widgetConfirmAbortRef = useRef<AbortController | null>(null);
  const latest = useRef({ dashboardSlug, onFallback, onWritten, t, logItemSeen, logClickCta, logPageClickBi, logLoadStart, logLoadFinish });
  latest.current = { dashboardSlug, onFallback, onWritten, t, logItemSeen, logClickCta, logPageClickBi, logLoadStart, logLoadFinish };

  // The read that opens the dialog, as a load: it gates the dialog the way a fetch-on-open does.
  const loadDialog = useCallback((read: Promise<PreparedCardAction>): Promise<PreparedCardAction> => {
    const started = Date.now();
    latest.current.logLoadStart(PREPARE_LOAD);
    return read.then((prepared) => {
      const isSuccessful = !(prepared.refused && prepared.reason === UNAVAILABLE);
      latest.current.logLoadFinish(PREPARE_LOAD, { isSuccessful, loadingTimeMs: Date.now() - started });
      return prepared;
    });
  }, []);

  const bi = useCallback((stage: CardActionStage, op: string, record: string, outcome: string) => {
    const ev = cardActionBiEvent(stage, op, record, outcome);
    const { logItemSeen: seen, logClickCta: click } = latest.current;
    (ev.type === 'itemSeen' ? seen : click)(ev.componentName, { additionalFields: ev.additionalFields });
  }, []);

  const refuse = useCallback(
    (op: string, record: string, reason: string, prompt: string) => {
      busyRef.current = false;
      bi('refused', op, record, reason);
      if (prompt) latest.current.onFallback(prompt);
      else setNotice({ message: latest.current.t('cardAction.failed'), severity: 'error' });
    },
    [bi],
  );

  const present = useCallback(
    (prepared: PreparedCardAction, op: string, record: string, undo: boolean, fallback: string) => {
      // `fallback` is the frame's prompt. Neither prompt follows a refused undo: both ask for the write itself,
      // and sending one would ask the agent to do the write again.
      const prompt = undo ? '' : prepared.fallbackPrompt || fallback;
      if (prepared.refused) {
        refuse(op, record, prepared.reason || UNAVAILABLE, prompt);
        return;
      }
      bi('shown', op, record, undo ? 'undo' : 'do');
      dialogRef.current = { prepared, op, record, undo, prompt };
      setDialog(dialogRef.current);
    },
    [bi, refuse],
  );

  const act = useCallback(
    (message: CardActMessage) => {
      const { op, record, valid } = checkedAct(message.op, message.record);
      if (busyRef.current || widgetConfirmAbortRef.current || !visibleRef.current) {
        bi('refused', op, record, BUSY);
        return;
      }
      if (!valid) {
        refuse(op, record, INVALID_REQUEST, message.fallback);
        return;
      }
      busyRef.current = true;
      const pending = { op, record, undo: false };
      preparingRef.current = pending;
      void loadDialog(
        prepareCardAction({
        dashboardSlug: latest.current.dashboardSlug,
        widgetSlug: message.widget,
        op,
        record,
          componentVersion: message.version,
        }),
      ).then((prepared) => {
        if (preparingRef.current !== pending) return;
        preparingRef.current = null;
        present(prepared, op, record, false, message.fallback);
      });
    },
    [bi, loadDialog, present, refuse],
  );

  const answer = useCallback(
    (ok: boolean, { notifyCustomer }: { notifyCustomer: boolean }) => {
      const open = dialogRef.current;
      if (!open) return;
      dialogRef.current = null;
      setDialog(null);
      if (!ok) {
        busyRef.current = false;
        bi('cancelled', open.op, open.record, open.undo ? 'undo' : 'do');
        return;
      }
      bi('confirmed', open.op, open.record, open.undo ? 'undo' : 'do');
      // The dialog answers `notifyCustomer: true` unless it offered the switch and the owner turned it off.
      void executeCardAction(open.prepared.token, { skipCustomerNotification: !notifyCustomer }).then((done) => {
        busyRef.current = false;
        const translate = latest.current.t;
        if (done.reason === OUTCOME_UNKNOWN) {
          bi('refused', open.op, open.record, done.reason);
          setNotice({ message: translate('cardAction.outcomeUnknown'), severity: 'error' });
          return;
        }
        if (!done.ok) {
          // Every other failure was refused before Wix wrote anything.
          refuse(open.op, open.record, done.reason, open.prompt);
          return;
        }
        bi(open.undo ? 'undone' : 'done', open.op, open.record, 'ok');
        clearWidgetToolResults();
        latest.current.onWritten();
        setNotice(
          open.undo
            ? { message: translate('cardAction.undone'), severity: 'success' }
            : {
                message: translate('cardAction.done'),
                severity: 'success',
                undo: done.undoToken ? { token: done.undoToken, op: open.op, record: open.record } : undefined,
              },
        );
      });
    },
    [bi, refuse],
  );

  const undo = useCallback(() => {
    const offer = notice?.undo;
    if (!offer || busyRef.current || widgetConfirmAbortRef.current) return;
    latest.current.logPageClickBi({ page_name: 'dashboard', button_name: 'card_action_undo' });
    busyRef.current = true;
    setNotice(null);
    const pending = { op: offer.op, record: offer.record, undo: true };
    preparingRef.current = pending;
    void loadDialog(undoPrepareCardAction(offer.token)).then((prepared) => {
      if (preparingRef.current !== pending) return;
      preparingRef.current = null;
      if (prepared.refused && prepared.reason !== TOKEN_INVALID) {
        // The token is still good: keep offering Undo, so a passing failure can be tried again.
        busyRef.current = false;
        bi('refused', offer.op, offer.record, prepared.reason || UNAVAILABLE);
        setNotice({ message: latest.current.t('cardAction.failed'), severity: 'error', undo: offer });
        return;
      }
      present(prepared, offer.op, offer.record, true, '');
    });
  }, [notice, bi, loadDialog, present]);

  /** Runs a widget's `sym.confirm` unless an act is under way or the dashboard is hidden, which answer it "no"
   *  without asking. `ask` gets the signal that withdraws the confirm when the dashboard is hidden. */
  const guardWidgetConfirm = useCallback((ask: (signal: AbortSignal) => Promise<boolean>): Promise<boolean> => {
    if (busyRef.current || !visibleRef.current) return Promise.resolve(false);
    const abort = new AbortController();
    widgetConfirmAbortRef.current = abort;
    return ask(abort.signal).finally(() => {
      if (widgetConfirmAbortRef.current === abort) widgetConfirmAbortRef.current = null;
    });
  }, []);

  const closeNotice = useCallback(() => setNotice(null), []);

  useEffect(() => {
    if (visible) return;
    const preparing = preparingRef.current;
    if (preparing) {
      preparingRef.current = null;
      busyRef.current = false;
      bi('cancelled', preparing.op, preparing.record, preparing.undo ? 'undo' : 'do');
    }
    const open = dialogRef.current;
    if (open) {
      dialogRef.current = null;
      setDialog(null);
      busyRef.current = false;
      bi('cancelled', open.op, open.record, open.undo ? 'undo' : 'do');
    }
    widgetConfirmAbortRef.current?.abort();
    setNotice(null);
  }, [visible, bi]);

  const element = (
    <>
      {dialog && <CardActionDialog key={dialog.prepared.token} prepared={dialog.prepared} undo={dialog.undo} onAnswer={answer} />}
      <Toast
        open={visible && notice != null}
        message={notice?.message ?? ''}
        severity={notice?.severity}
        autoHideDuration={notice?.undo ? UNDO_WINDOW_MS : undefined}
        action={notice?.undo ? { label: t('cardAction.undo'), onClick: undo } : undefined}
        onClose={closeNotice}
      />
    </>
  );

  return { act, guardWidgetConfirm, element };
}
